import { type ComponentChildren } from 'preact';
import { useErrorBoundary } from 'preact/hooks';

/**
 * Route-scoped error boundary. Without one, a throw during a page's render (e.g. a missing i18n key,
 * a malformed API row) is uncaught — Preact's reconciliation is left half-committed, orphaning DOM
 * and duplicating the whole page on the next render (the "content stacks on click" bug). With a
 * boundary, Preact cleanly unmounts the failed subtree and renders this fallback instead, so a
 * single page's crash can never corrupt the shell or the rest of the app.
 *
 * Placed INSIDE the per-route keyed wrapper (ShellLayout), so navigating to another route remounts
 * the boundary and clears the error automatically — the operator is never stuck on a dead screen.
 */
export function RouteErrorBoundary({ children }: { children: ComponentChildren }) {
  const [error, reset] = useErrorBoundary();

  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div
        role="alert"
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: '60vh',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: '32rem', display: 'grid', gap: '0.75rem' }}>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>This screen hit an error</h1>
          <p style={{ margin: 0, opacity: 0.75 }}>
            Something went wrong rendering this page. Try again, or switch to another screen — the rest of the
            app is unaffected.
          </p>
          <pre
            style={{
              margin: '0.5rem 0 0',
              padding: '0.75rem',
              borderRadius: '8px',
              background: 'rgba(127,127,127,0.12)',
              fontSize: '0.8rem',
              overflowX: 'auto',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
            }}
          >
            {message}
          </pre>
          <div>
            <button
              type="button"
              onClick={() => reset()}
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: '1px solid currentColor',
                background: 'transparent',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
